import React, { useState } from "react";
import { Plus, Package } from "lucide-react";
import {
  AddPackageModal,
  PackageFormInput,
} from "../../components/sunbeds/AddPackageModal";
import { SendReminderModal } from "../../components/sunbeds/SendReminderModal";
import { DeleteConfirmationModal } from "../../components/sunbeds/DeleteConfirmationModal";
import { Button } from "../../components/ui/Button";
import {
  getSunbeds,
  createSunbed,
  updateSunbed,
  deleteSunbed,
  SunbedItem,
  CreateSunbedPayload,
} from "../../services/sunbeds";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { SunbedActionToolbar } from "../../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import { SunbedPackagesTable } from "../../components/sunbeds/SunbedPackagesTable";

const SunbedPackages: React.FC = () => {
  const [packages, setPackages] = useState<PackageFormInput[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(8);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PackageFormInput | null>(
    null
  );
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [isReminderOpen, setIsReminderOpen] = useState<boolean>(false);
  const [reminderTarget, setReminderTarget] = useState<PackageFormInput | null>(
    null
  );
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchPackages = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getSunbeds();

      // Format the backend response for our UI
      const viewData: PackageFormInput[] = res.data.map((item: SunbedItem) => {
        // Some APIs return pricing under `pricing_data`, others under `pricing`
        const rawPricing = (item as any).pricing_data || (item as any).pricing || [];

        return {
          id: item.id.toString(),
          label: item.label,
          status: item.status as "active" | "inactive",
          // Keep the full room objects so the table can display labels/status,
          // while the modal will normalise these into room ids when editing.
          sunbed_room: (item.rooms || []).map((room: any) => ({
            id: room.id?.toString?.() ?? String(room.id ?? room.label),
            label: room.label,
            status: room.status,
            totalDuration: room.total_duration || "",
          })),
          pricing: rawPricing.map((price: any) => ({
            id: price.id ? price.id.toString() : `${Math.random()}`,
            label: price.label,
            type: price.type as "per_minute" | "fixed",
            price: Number(price.price),
            duration: price.duration ? parseInt(String(price.duration)) : 0,
          })),
        };
      });

      setPackages(viewData);
    } catch (error: any) {
      console.error("Error loading packages:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  React.useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const openReminderModal = (pkg: PackageFormInput) => {
    setReminderTarget(pkg);
    setIsReminderOpen(true);
  };

  const onSendReminder = (message: string) => {
    // TODO: Connect to backend endpoint for reminders
    console.log("Sending reminder to:", reminderTarget?.label, "Message:", message);
    setIsReminderOpen(false);
  };

  // Filter list based on search
  const filteredPackages = packages.filter((pkg) =>
    pkg.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentViewPackages = filteredPackages.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Reset to page 1 if search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const openAddModal = () => {
    setModalMode("add");
    setEditingPackage(null);
    setIsModalOpen(true);
  };

  const openEditModal = (pkg: PackageFormInput) => {
    setModalMode("edit");
    setEditingPackage(pkg);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (formData: PackageFormInput) => {
    try {
      // Prepare data for API
      const apiPayload: CreateSunbedPayload = {
        label: formData.label,
        status: formData.status,
        // Normalise union room values to a pure list of room ids (strings)
        sunbed_room: formData.sunbed_room.map((room) =>
          typeof room === "string" ? room : room.id
        ),
        pricing: formData.pricing.map((p) => ({
          label: p.label,
          type: p.type as "per_minute" | "fixed",
          price: p.price,
          duration: p.duration,
        })),
      };

      if (modalMode === "add") {
        await createSunbed(apiPayload);
      } else if (editingPackage?.id) {
        await updateSunbed(Number(editingPackage.id), apiPayload);
      }

      await fetchPackages();
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("Save error:", error);
      alert(error.message || "Could not save package.");
    }
  };

  const requestDelete = (id: string) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const executeDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteSunbed(Number(deleteId));
      await fetchPackages();
      setDeleteId(null);
    } catch (error: any) {
      console.error("Delete error:", error);
      alert(error.message || "Could not delete package.");
    } finally {
      setIsDeleteOpen(false);
    }
  };

  return (
    <div>
      <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
        <SubPageHeader
          title="Sunbed Packages"
          subtitle="Manage your sunbed packages, pricing tiers, and room assignments"
          icon={Package}
          actions={
            <Button
              onClick={openAddModal}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-5 py-2.5 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <Plus size={18} />
              Create New Package
            </Button>
          }
        />

        <SunbedActionToolbar
          totalItems={packages.length}
          showingStart={currentViewPackages.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filteredPackages.length)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search packages..."
        />

        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
          <SunbedPackagesTable
            packages={currentViewPackages}
            isLoading={isLoading}
            indexOfFirstItem={indexOfFirstItem}
            onEdit={openEditModal}
            onDelete={requestDelete}
            onReminder={openReminderModal}
          />

          <PaginationFooter
            totalItems={packages.length}
            filteredTotal={filteredPackages.length}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <AddPackageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleFormSubmit}
        mode={modalMode}
        existingPackage={editingPackage}
      />

      <SendReminderModal
        isOpen={isReminderOpen}
        onClose={() => setIsReminderOpen(false)}
        onSend={onSendReminder}
        title={
          reminderTarget
            ? `Send Reminder: ${reminderTarget.label}`
            : "Send Reminder"
        }
      />

      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={executeDelete}
        title="Delete Package"
        message="Are you sure you want to delete this package? This action cannot be undone."
      />
    </div>
  );
};

export default SunbedPackages;
